import React, { useState } from "react";
import className from "classnames";
import PropTypes from "prop-types";
import classNames from "classnames";

import ProcessPage from "components/ProcessPage";
import Button from "components/Button";
import Modal from 'components/CardModal';
import SingleRange from 'components/Filter/types/SingleRange'
import ActionBoxRow from "../../../../components/ActionBoxRow/ActionBoxRow";
import { LIBRARY_ACCENT } from 'utils/constants';
import { defaultNotifier, elementNotifier } from 'functions/notificationHandler';
import { augmentDataset } from 'api/dataset'
import { AddPlus, TickIcon3, CloseIcon, EditPencilIcon } from 'images/common';
import { mapAugmentationConfiguration, generateConfigurations, countAugmentationTypes, generationLimitCheck } from "./utils";

const AugmentationConfirmation = (props) => {
  const { color = LIBRARY_ACCENT, onClose, onBack, selectedAugmentations, dataset, onAugmentationClick, changeStep, setSelectedEditor, blurTypes, noiseTypes, cropTypes, useDefaultSettings, toggleUseDefaultSettings, onConfirm } = props
  const [submitLoading, setSubmitLoading] = useState(false)
  const [expansion, setExpansion] = useState('2.0')
  const formatAugmentations = mapAugmentationConfiguration({ selected: selectedAugmentations, blurTypes, noiseTypes, cropTypes, useDefaultSettings })
  const countAugmentations = countAugmentationTypes({ augmentationTypes: formatAugmentations, blurTypes, noiseTypes, cropTypes, useDefaultSettings })
  const limitInfo = generationLimitCheck(Number(dataset.dataSplit?.original), Number(countAugmentations), Number(expansion))

  const onSubmit = async () => {
    setSubmitLoading(true)
    const configuration = generateConfigurations({ selectedAugmentations, useDefaultSettings, blurTypes, noiseTypes, cropTypes });

    const payload = {
      ...configuration,
      expansionFactor: Number(expansion),
      datasetId: dataset.id
    }

    const { data } = await augmentDataset(payload);

    if (data.errors) {
      defaultNotifier(data.errors)
      setSubmitLoading(false)
      return
    }

    elementNotifier('notification_augmentation', <div><strong>Generating Additional Images</strong><br /> This process may take anywhere from a few minutes up to an hour, depending on the size of the dataset. We'll notify you as soon as dataset is ready.</div>)
    onConfirm()
  }

  const submitClass = className("button", {
    [`button_${color}`]: color,
  });

  const handleCloseModal = () => {
    if (onClose) onClose();
  };

  const onRemoveClick = (clicked) => {
    const getTypeData = selectedAugmentations.find(item => item.key === clicked.key)
    onAugmentationClick(getTypeData)
  }

  const onEditClick = (clicked) => {
    toggleUseDefaultSettings(false)
    const getTypeData = selectedAugmentations.find(item => item.key === clicked.key)
    changeStep(2)
    setSelectedEditor(getTypeData)
  }

  return (
    <ProcessPage onCancel={handleCloseModal} onBack={onBack} customClass='augmentation-popup augmentation-popup--confirmation'>
      {() => (
        <div className="popup__cont">
          <div className="login">
            <div className="headline">
              <h1 className="headline__title">Launch Data Generation</h1>
              <div className="headline__subtitle">
                Finalize how your dataset will be expanded.
              </div>
            </div>
            <div>
              <Modal
                title={'Augmentations'}
                subHeader="Review selected configurations."
                titleClass="modal__title_label"
                onAction={() => changeStep(1)}
                actionIcon={<AddPlus />}
                actionLabel={'Add'}
                body={(
                  <ActionBoxRow
                    rows={formatAugmentations}
                    color={color}
                    isCard
                    hasBackground
                    action={{
                      type: 'button',
                      onClick: (item) => onRemoveClick(item),
                      icon: <CloseIcon />
                    }}
                    hoverActions={[
                      {
                        onClick: (item) => onEditClick(item),
                        icon: <EditPencilIcon />
                      }
                    ]}
                    showAll={formatAugmentations.length > 5 ? 5 : null}
                    cellDefs={[
                      {
                        key: 'name',
                        icon: <TickIcon3 />,
                        className: 'viso-font-medium',
                        iconColor: color
                      },
                      {
                        key: 'content',
                        formatter: (value) => value.map((item, index) => <ul className='viso-list-bullets' key={index}><li>{item}</li></ul>),
                        textColor: 'label'
                      }
                    ]}
                  />
                )}
                onClose={handleCloseModal}
              />

              <Modal
                title={'Expand Dataset'}
                subHeader="Enter a numeric value or adjust the slider to indicate how much you want to enlarge your dataset (e.g., 2× for doubling). A higher factor will generate more images, which can lead to improved model performance, though it may require additional processing time."
                titleClass="modal__title_label"
                body={(
                  <>
                    <div className={classNames("width-240 mt_50 mb_150", color)}>
                      <SingleRange
                        readOnly
                        name="expansion"
                        title="Expansion Factor"
                        onChange={(val) => {
                          setExpansion(Number(val).toFixed(1))
                        }}
                        value={expansion}
                        maxValue={4}
                        minValue={1.5}
                        step={0.5}
                        color={color}
                        showLegend
                        isSmall
                        legendColor='border'
                        borderColor='border'
                        customClass='nomargin'
                        unit='x'
                      />
                    </div>
                    <div>
                      {!limitInfo.isValid &&
                        <div className="modal_info modal_info--block viso-bg-danger-10 viso-text-danger">
                          <strong>Caution:</strong> Image generation limits exceeded.
                          <ul className='viso-list-bullets mt_50'>
                            {limitInfo.isDatasetLimitExceeded && (
                              <li>
                                Too many files - The maximum size for a dataset is {limitInfo.maxDatasetItems.toLocaleString()} items. This configuration would create {limitInfo.actualImages.toLocaleString()} items.
                              </li>
                            )}
                            {limitInfo.isGeneratedImagesLimitExceeded && !limitInfo.isDatasetLimitExceeded && (
                              <li>
                                Too many generated images for this dataset size. With {limitInfo.images.toLocaleString()} original items you can generate up to {limitInfo.maxImages.toLocaleString()} items, but this configuration would create {limitInfo.actualImages.toLocaleString()} items.
                              </li>
                            )}
                            {limitInfo.isScaleLimitExceeded && (
                              <li>
                                Expansion factor too high - With {limitInfo.images.toLocaleString()} original items you can expand up to {limitInfo.maxScale}×.
                              </li>
                            )}
                            {limitInfo.isTypeLimitExceeded && (
                              <li>
                                Too many augmentation types - For datasets of this size you can apply up to {limitInfo.maxTypes} augmentation types, but you selected {limitInfo.types}.
                              </li>
                            )}
                          </ul>
                        </div>
                      }
                      <div className="modal_info">
                        <div className="modal_info__content">
                          <div>Original size</div>
                          <div>Augmented size</div>
                          <div>Augmentations</div>
                        </div>
                        <div className="modal_info__divider" />
                        <div className="viso-text-text">
                          <div>{dataset.dataSplit?.original} images</div>
                          <div>{Number(dataset.dataSplit?.original) * Number(expansion)} images</div>
                          <div>{countAugmentations} types</div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
                onClose={handleCloseModal}
              />

            </div>
            <div className="login__actions">
              <Button
                dataCy="submit-model"
                disabled={!limitInfo.isValid || selectedAugmentations.length === 0}
                onClick={onSubmit}
                className={submitClass}
                isLoading={submitLoading}
              >
                Generate now
              </Button>
            </div>
          </div>
        </div>
      )}
    </ProcessPage>
  );
}

AugmentationConfirmation.propTypes = {
  color: PropTypes.string,
  onClose: PropTypes.func
}

export default AugmentationConfirmation